import { useSnackbarContext } from '@/context/Snackbar/SnackbarContext';
import { useGetDeliveryInfoData } from '@/hooks/order/details/useGetDeliveryInfo';
import { OrderDeliveryInfoResponseToRemissionUi } from '@/hooks/order/details/useGetDeliveryInfo/mapper';
import { useUpdateRemission } from '@/hooks/order/useUpdateRemission';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '@/utils/fileUploadUtils';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { initialState, RemissionAssetUi } from './RemissionUi';

export function useRemissionViewController() {
  const [uiState, setUiState] = useImmer<RemissionAssetUi>(initialState);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<File | null>(null);
  const { showSnackError } = useSnackbarContext();
  const updateRemissionHook = useUpdateRemission();
  const orderId = useParams().orderDetailId ?? '';
  const { data } = useGetDeliveryInfoData(orderId);

  useEffect(() => {
    if (!data) return;
    setUiState(OrderDeliveryInfoResponseToRemissionUi(data));
  }, [data]);

  const onClickedEditAsset = () => fileInputRef.current?.click();

  const onClickedDeleteAsset = () =>
    setUiState((draft) => {
      draft.asset = undefined;
      draft.cancelDisabled = false;
      draft.saveDisabled = true;
    });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      showSnackError(`El archivo no puede ser mayor a ${MAX_FILE_SIZE_MB} MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    fileRef.current = file;

    if (file.type === 'application/pdf') {
      setUiState((draft) => {
        draft.asset = { kind: 'pdfPendingToSaveAsset' };
        draft.saveDisabled = false;
        draft.cancelDisabled = false;
      });
    } else {
      const reader = new FileReader();
      reader.onload = () =>
        setUiState((draft) => {
          draft.asset = {
            kind: 'imagePendingToSaveAsset',
            data: reader.result as string,
          };
          draft.saveDisabled = false;
          draft.cancelDisabled = false;
        });
      reader.readAsDataURL(file);
    }
  };

  const onClickedAsset = () => {
    const { asset } = uiState;
    if (asset?.kind === 'pdfSavedAsset' || asset?.kind === 'imageSavedAsset') {
      window.open(asset.url, '_blank');
    }
  };

  const onClickedCancel = () => {
    if (!data) return;
    setUiState(OrderDeliveryInfoResponseToRemissionUi(data));
  };

  const onClickedSave = () => {
    if (!orderId || !fileRef.current) return;
    updateRemissionHook.updateImage({ orderId, file: fileRef.current });
  };

  return {
    uiState,
    actions: {
      onClickedEditAsset,
      onClickedDeleteAsset,
      handleFileChange,
      onClickedAsset,
      onClickedCancel,
      onClickedSave,
    },
    references: { fileInputRef },
  };
}
